define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/dom-class",
	"dojo/dom-style",
	"dojo/keys",
	"dojo/regexp",
	"dojo/string",
	"dojo/when",
	"csi",
	"./LanguageUtil",
	"csi/html",
	"csi/locale",
	"csi/store/common",
	"csi/store/DelayedRequest",
	"csi/store/DelayedFilteringRequest",
	"csi/utility/stringUtility",
	"csi/xml",
	"csi/widget/CheckBox",
	"csi/widget/MessageDialog",
	"./_ComboBoxMenu"
], function(declare, lang, aspect, domClass, domStyle, keys, regexp, string, when, csi, LanguageUtil, html, locale, {DISABLED_PROPERTY}, {DelayedRequest}, {DelayedFilteringRequest}, {searchStringEquals}, xml, CheckBox, MessageDialog, _ComboBoxMenu) {

	return declare(null, {
		highlightMatch: "all",
		pageSize: 20,
		queryExpr: "*${0}*",
		searchDelay: 10,

		/** @property {boolean} acceptsMultipleValues - indicates drop down can have multiple values selected simultaneously */
		acceptsMultipleValues: false,

		// specify whether drop down should be open on focus
		bOpenOnFocus: false,

		/** @property {boolean} containSelection - true if pagination is present and current page contains selected item */
		//containSelection: false,

		// specify our own drop-down to handle disabled items
		dropDownClass: _ComboBoxMenu,

		// Don't close drop down when filter value produce no results. See FilteringSelectInDropdown for usage
		// dontCloseNoResults: false,


		/** @property {boolean} hasPlaceholder - indicates drop down should be shown even when no results */
		hasPlaceholder: false,

		// the additional CSS class assigned to the drop-down menu
		//dropDownCssClass: null,

		/** @property {string} initialDisplayedValue - intial value for displayedValue property */
		initialDisplayedValue: null,

		/** @property {boolean} renderSlimCards - indicates if options will be rendered as slim cards */
		renderSlimCards: false,

		// Object: handles to store events
		//_handleRequest: null,
		//_handleResponse: null,

		// Object: search characters that already come in escaped
		_csiEscapes: {
			"\\\*": true,
			"\\\?": true,
			"\\\\": true
		},

		// String: prefix to query string added when auto-completing single result
		//_csiPrefix: null,

		destroy: function() {
			if (this._handleRequest) {
				this._handleRequest.remove();
				this._handleResponse.remove();
				this._handleRequest = this._handleResponse = null;
			}

			this._abortQuery();
			this.inherited(arguments);
		},

		doHighlight: function(/*String*/label, /*String*/find) {
			// summary: highlight matches in any part of string, not just start of word

			// if the locale is in China or other countrys don't use English, highlight the words from the label
			if (LanguageUtil.isPinyinEnabled()) {
				var tempLetter = LanguageUtil.getPinyin(label);
				var pos = tempLetter.indexOf(find.toLowerCase());
				if (pos > -1) {
					// highlight the word corresponding to the user input if user use the other languages rather than English
					find = label.substring(pos, pos + find.length);
				}
			}

			// C8-9829 - ensure that we only match actual text (possibly with <, >, and " characters) rather than escaped text (&lt;, &gt;, &amp;),
			// but then need to escape the result
			var SPAN1 = "LTspan class=QdijitComboBoxHighlightMatchQGT";
			var SPAN2 = "LT/spanGT";
			var escapedFind = this._csiEscapes[find] ? find : regexp.escapeString(this._escapeHtml(find));
			var result = label.replace(new RegExp("()(" + escapedFind + ")", "ig"), '$1' + SPAN1 + '$2' + SPAN2);
			result = this._escapeHtml(result);
			return result.replace(new RegExp(SPAN1, "g"), '<span class="dijitComboBoxHighlightMatch">').replace(new RegExp(SPAN2, "g"), '</span>');
		},

		/**
		 * Enabled delayed search for component
		 * @param {StoreNames} store
		 * @param {VoidFunction} [onPlaceholderGoToDialog] - indicate if Go to Dialog is an option, and if so, what to do when used
		 */
		enableDelayedSearch(store, onPlaceholderGoToDialog) {
			const {dropDownClass} = this;
			const delayedStore = new DelayedRequest(store);
			let onGoToDialog;

			if (onPlaceholderGoToDialog) {
				onGoToDialog = () => {
					this.closeDropDown();
					onPlaceholderGoToDialog();
				};
			}

			this.set("store", delayedStore);
			this.hasPlaceholder = true;
			this.dropDownClass = declare(dropDownClass, {
				delayedStore,
				onPlaceholderParams: () => ({
					hint: this.getDelaySearchHint(),
					onGoToDialog,
				})
			});
		},

		/**
		 * Enabled delayed filtering for component
		 * @param {StoreNames} store
		 * @returns {StoreNames}
		 */
		enableDelayedFiltering(store) {
			const {dropDownClass, recentValues} = this;
			const delayedFilteringStore = this.dropDown?.delayedFilteringStore || new DelayedFilteringRequest(store, recentValues);

			this.set("store", delayedFilteringStore);
			this.hasPlaceholder = true;
			this.dropDownClass = declare(dropDownClass, {
				delayedFilteringStore,
			});

			// update exclusiveItems 
			this.exclusiveItems = delayedFilteringStore.exclusiveItems;
			return delayedFilteringStore;
		},

		focus: function() {
			// summary: start search when widget comes into focus if so configured
			this.inherited(arguments);

			if (!this._opened && this.bOpenOnFocus) {
				this._startSearchAll();
			}
		},

		/**
		 * Return true if a query is in progress
		 * @return {boolean}
		 */
		isQueryInProgress() {
			return !!this._queryDeferHandle || !!this._fetchHandle;
		},

		openDropDown: function() {
			// summary: workaround for width of menu not expanding when "More choices" yields wider options;
			// problem is that once width style is applied, it cannot grow again, so reset the width each time
			const {autoWidth, dropDown, forceWidth} = this;
			const ddNode = dropDown?.domNode;

			if (ddNode && autoWidth && !forceWidth) {
				domStyle.set(ddNode, "width", "");
			}

			this.inherited(arguments);
		},

		/**
		 * Update widget to render with a slim card
		 * @param {boolean} value
		 */
		_setRenderSlimCardsAttr(value) {
			this._set("renderSlimCards", value);
			this.set("labelType", value ? "html" : "text");
			this.set("labelFunc", value ? this._slimCardLabelFunc : null);
			this.dropDown?.domNode.classList.toggle("csi-combo-box-slim-card", value);
		},

		/**
		 * Return HTML label to display slim card for store item
		 * @param {NamesResult} item 
		 * @param {any} store 
		 * @return {string}
		 */
		_slimCardLabelFunc(item, store) {
			// the store passed into the function may be an internal (Memory) store, so make sure to get the label properties from the main widget store
			const {labelProperty, labelProperty2} = this.store;
			const label1 = item[labelProperty];
			const label2 = item[labelProperty2];
			if (label2) {
				const div = html.create("div", {className: "csi-combo-box-slim-card-label"});
				html.create("div", {
					className: "csi-ellipsis-nowrap",
					innerText: label1,
					title: label1,
				}, div);
				html.create("div", {
					className: "csi-ellipsis-nowrap",
					innerText: label2,
					title: label2,
				}, div);
				return div.outerHTML;
			}

			return label1;
		},

		_showResultList: function() {
			this.inherited(arguments);
			if (this.showInvalidMessageAttr && this.item && this.item[this.showInvalidMessageAttr]) {
				this.displayMessage(this.showInvalidMessage);
			}
		},

		_announceOption: function(/*Node*/ node) {
			// summary:
			//		a11y code that puts the highlighted option in the textbox.
			//		This way screen readers will know what is happening in the
			//		menu.

			if (!node || domClass.contains(node, html.CSS_DISABLED_SELECTION)) {
				return;
			}

			// pull the text value from the item attached to the DOM node

			// get the text that the user manually entered (cut off autocompleted text)

			// CENTRIC - this is needed to work with the substring matching provided
			// above in doHighlight - at this point we have a single matching item, so
			// dojo auto-completes the string. When this happens, it highlights the string
			// starting from the first character with as many characters as have been typed
			// by the user (_lastInput). If what the user typed did not match from the start,
			// then this does not work. So we have to add to what the user has typed with the
			// string from the start (_csiPrefix).
			//
			// If node does not match lastInput, then it could be the "Go to Dialog" action.
			// In that case, do not auto-complete.
			var item = this.dropDown.items[node.getAttribute("item")];

			if (this.store && item && item[this.store.idProperty] == csi.DIALOG_URL) {
				//if filter search text typed in and go to dialog clicked _lastInput may contain the typed in text so clear it
				this._lastInput = "";
			}

			var oldLastInput = this._lastInput;
			var text = node.textContent || node.innerText || "";
			var translatedText = oldLastInput;

			//if the locale is in China or other country which don't use English
			if (LanguageUtil.isPinyinEnabled()) {
				//convert the text into english letter, if it's English, keep the original version
				text = LanguageUtil.getPinyin(text);
				translatedText = LanguageUtil.getPinyin(translatedText);
			}

			var s = text.toLowerCase();
			var n = s.indexOf(translatedText.toLowerCase());

			if (n >= 0 || this.hasActionsWithoutAutocomplete()) {
				//check if autoComplete is off and store action data exists, if exists call announce option to set item
				this._csiPrefix = text.substr(0, n);
				this._lastInput = this._csiPrefix + this._lastInput;
				this.inherited(arguments);
				this._lastInput = oldLastInput;
			}

		},

		/**
		 * @override
		 * Skip highlighting for action items
		 */
		_getMenuLabelFromItem(item) {
			const {store} = this;

			if (store && item[store.actionProperty]) {
				return {
					html: this.labelType === "html",
					label: this.labelFunc(item, store),
				};
			}

			return this.inherited(arguments);
		},

		_openResultList: function(/*Object*/ results, /*Object*/ query, /*Object*/ options) {
			// check that node is visible before opening - it is possible that in the middle of search the DOM
			// has changed and the node is no longer visible, or even in the DOM
			if (!this.domNode.offsetHeight && !this.extraTextBox) {
				return;
			}

			// DOJO INTERNAL: only autoComplete if one result
			// make sure to compare against real results rather than actions posing as results
			// FIX: don't like doing it this way, but it works; would like to override the dropDown menu
			// with extra action, but ComboBox is too tightly integrated with ComboBox menu
			var ad = this.store.actionData;
			var n = 1 + (ad ? ad.length : 0);
			var b = results.length != n;

			if (this.dropDown && this.classAttr) {
				this.dropDown.classAttr = this.classAttr;
			}

			if (this._prev_key_backspace) {
				this._openResultListDojo(results, query, options);
			} else {
				this._prev_key_backspace = b;
				this._openResultListDojo(results, query, options);
				this._prev_key_backspace = false;
			}

			// if going from one announced result to multiple results, then reset valueNode to real _lastInput
			if (b && this._csiPrefix) {
				this.focusNode.value = this._lastInput;
				this._csiPrefix = null;
			}
		},

		_openResultListDojo: function(/*Object*/ results, /*Object*/ query, /*Object*/ options) {
			// summary:
			//		Callback when a search completes.
			// description:
			//		1. generates drop-down list and calls _showResultList() to display it
			//		2. if this result list is from user pressing "more choices"/"previous choices"
			//			then tell screen reader to announce new option
			var wasSelected = this.dropDown.getHighlightedOption();
			this.dropDown.clearResultList();
			this.dropDown.containSelection = false;

			// CENTRIC - begin
			if (!this.hasPlaceholder && !this.dontCloseNoResults) {
				if (!results.length && options.start == 0) { // if no results and not just the previous choices button
					this.closeDropDown();
					return;
				}
			}
			// CENTRIC - end

			this._nextSearch = this.dropDown.onPage = lang.hitch(this, function(direction) {
				results.nextPage(direction !== -1);
				this.focus();
			});

			const store = this.store;

			if (!!this.item && !this.acceptsMultipleValues) {
				const currentItemValue = store.getIdentity(this.item);
				this.dropDown.isSelectedFunc = (item) => (!!item && currentItemValue === store.getIdentity(item));
			}

			// Fill in the textbox with the first item from the drop down list,
			// and highlight the characters that were auto-completed. For
			// example, if user typed "CA" and the drop down list appeared, the
			// textbox would be changed to "California" and "ifornia" would be
			// highlighted.

			this.dropDown.createOptions(
				results,
				options,
				lang.hitch(this, "_getMenuLabelFromItem")
			);

			// show our list (only if we have content, else nothing)
			this._showResultList();

			if (this.dropDown.domNode.children) {
				// add title tags for items that are longer than the dropDown max width
				$(this.dropDown.domNode.children).bind('mouseenter', function() {
					var $this = $(this);
					if (this.offsetWidth < this.scrollWidth && !$this.attr('title')) {
						$this.attr('title', $this.text());
					}
				});
			}

			// #4091:
			//		tell the screen reader that the paging callback finished by
			//		shouting the next choice
			if ("direction" in options) {
				const { isSelectedFunc, items} = this.dropDown;
				// assign and compare, "=" is correct, not typo
				if (!(this.dropDown.containSelection = items.some(item=>isSelectedFunc?.(item)))) {
					if (options.direction) {
						this.dropDown.highlightFirstOption();
					} else if (!options.direction) {
						this.dropDown.highlightLastOption();
					}
				}
				if (wasSelected) {
					this._announceOption(this.dropDown.getHighlightedOption());
				}
			} else if (this.autoComplete && !this._prev_key_backspace
				// when the user clicks the arrow button to show the full list,
				// startSearch looks for "*".
				// it does not make sense to autocomplete
				// if they are just previewing the options available.
				&& !/^[*]+$/.test(query[this.searchAttr].toString())) {
				this._announceOption(this.dropDown.containerNode.firstChild.nextSibling); // 1st real item
			}
		},

		_patternToRegExp: function(pattern) {
			// summary: if pattern is empty, then do not invoke multi-line matching
			// reference: https://bugs.dojotoolkit.org/ticket/18334
			if (!pattern) {
				return new RegExp("^$");
			}

			// if it's not in China or contains Chinese characters, just use the original one;
			// also no need to test for pinyin characters if the search string (pattern) is empty
			if (!xml.queryFromMatchValue(pattern) ||
				!LanguageUtil.isPinyinEnabled() ||
				LanguageUtil.containsChinese(pattern)) {
				return this.inherited(arguments);
			}

			// in some cases, the input source tool will add extra symbols, strip out it
			pattern = LanguageUtil.trimExtraSymbol(pattern);
			const re = this.inherited(arguments, [pattern]);

			return {
				test: (value, item) => re.test(LanguageUtil.getPinyin(value), item)
			};
		},

		_setItemAttr: function(/*item*/ item, /*Boolean?*/ priorityChange, /*String?*/ displayedValue) {
			const store = this.store;

			this.inherited(arguments);

			if (item && (item[DISABLED_PROPERTY] || (store._oldAPI && store.getValue(item, DISABLED_PROPERTY)))) {
				domClass.add(this.textbox, html.CSS_DISABLED_SELECTION);
			} else {
				domClass.remove(this.textbox, html.CSS_DISABLED_SELECTION);
			}

			// reset initial value when an item is selected
			if (item) {
				this.initialDisplayedValue = null;
			}
		},

		_setStoreAttr: function(/*Store*/store) {
			// summary: cancel current query and set new store
			if (this.dropDown) {
				// make sure to close drop down before destroying it
				this.closeDropDown();
				this.dropDown.destroyRecursive();
				delete this.dropDown;
			}

			if (this._handleRequest) {
				this._postRequest();
				this._handleRequest.remove();
				this._handleResponse.remove();
				this._handleRequest = this._handleResponse = null;
			}

			// add store to fetchProperties to check for disabled items (see _ComboBoxMenu).
			this.fetchProperties.store = store;

			if (store) {
				const oldStore = this.get("store");
				const {classAttr, searchAttr} = this;
				const {cssProperty, labelProperty, searchProperty} = store;
				const newSearchAttr = searchProperty || labelProperty;
				const oldSearchAttr = oldStore?.searchProperty || oldStore?.labelProperty;

				// general support for store.labelProperty - if searchAttr is default value or is set to previous
				// store's label property, then update with new store's label property
				if ((searchAttr === "name") || (searchAttr === oldSearchAttr)) {
					this.searchAttr = newSearchAttr || "name";
				}

				if (cssProperty && !classAttr) {
					this.classAttr = cssProperty;
				}

				this.inherited(arguments);

				// FIX Dojo 1.7 - we really want to watch the _fetchHandle here, but hopefully this will work just as well
				if (store.onRequest) {
					this._handleRequest = aspect.after(store, "onRequest", lang.hitch(this, this._preRequest));
					this._handleResponse = aspect.after(store, "onResponse", lang.hitch(this, this._postRequest));
				}
			} else {
				// FIX - _setStoreAttr does not tolerate null, so set directly here
				this._set("store", null);
			}
		},

		/** override method to fix paging (More/Previous) when grouping */
		_startSearch: function(/*String*/ text) {
			// CENTRIC - START
			var bAddClass = (this.dropDown == null);
			// CENTRIC - END

			// DOJO - From _AutoCompleterMixin.js
			// summary:
			//		Starts a search for elements matching key (key=="" means to return all items),
			//		and calls _openResultList() when the search completes, to display the results.
			if (!this.dropDown) {
				var popupId = this.id + "_popup",
					dropDownConstructor = lang.isString(this.dropDownClass) ?
						lang.getObject(this.dropDownClass, false) : this.dropDownClass;
				this.dropDown = new dropDownConstructor({
					onChange: lang.hitch(this, this._selectOption),
					id: popupId,
					dir: this.dir,
					textDir: this.textDir
				});

				if (this.renderSlimCards) {
					this.dropDown.domNode.classList.add("csi-combo-box-slim-card");
				}
			}
			this._lastInput = text; // Store exactly what was entered by the user.

			// DOJO - From _SearchMixin.js
			// summary:
			//		Starts a search for elements matching text (text=="" means to return all items),
			//		and calls onSearch(...) when the search completes, to display the results.

			this._abortQuery();
			var
				_this = this,
				currentQuery = "",
				// Setup parameters to be passed to store.query().
				// Create a new query to prevent accidentally querying for a hidden
				// value from FilteringSelect's keyField
				query = lang.clone(this.query), // #5970
				options = {
					start: 0,
					count: this.pageSize,
					queryOptions: {		// remove for 2.0
						ignoreCase: this.ignoreCase,
						deep: true
					}
				},
				qs = string.substitute(this.queryExpr, [text.replace(/([\\\*\?])/g, "\\$1")]),
				q,
				startQuery = function() {
					var resPromise = _this._fetchHandle = _this.store.query(query, options);
					if (_this.disabled || _this.readOnly || (q !== currentQuery)) {
						return;
					} // avoid getting unwanted notify
					when(resPromise, function(res) {
						_this._fetchHandle = null;
						if (!_this.disabled && !_this.readOnly && (q === currentQuery)) { // avoid getting unwanted notify
							when(resPromise.total, function(total) {
								res.total = total;
								var pageSize = _this.pageSize;
								if (isNaN(pageSize) || pageSize > res.total) {pageSize = res.total;}
								// Setup method to fetching the next page of results
								res.nextPage = function(direction) {
									//	tell callback the direction of the paging so the screen
									//	reader knows which menu option to shout
									options.direction = direction = direction !== false;
									options.count = pageSize;
									if (direction) {
										// CENTRIC - START
										options.start += pageSize;  // res.length; - when grouping, each group counts extra and messes up paging
										// CENTRIC - END

										if (options.start >= res.total) {
											options.count = 0;
										}
									} else {
										options.start -= pageSize;
										if (options.start < 0) {
											options.count = Math.max(pageSize + options.start, 0);
											options.start = 0;
										}
									}
									if (options.count <= 0) {
										res.length = 0;
										_this.onSearch(res, query, options);
									} else {
										startQuery();
									}
								};
								_this.onSearch(res, query, options);
							});
						}
					}, function(err) {
						_this._fetchHandle = null;
						if (!_this._cancelingQuery) {	// don't treat canceled query as an error
							console.error(_this.declaredClass + ' ' + err.toString());
						}
					});
				};

			lang.mixin(options, this.fetchProperties);

			// Generate query
			if (this.store._oldAPI) {
				// remove this branch for 2.0
				q = qs;
			} else {
				// Query on searchAttr is a regex for benefit of dojo/store/Memory,
				// but with a toString() method to help dojo/store/JsonRest.
				// Search string like "Co*" converted to regex like /^Co.*$/i.
				q = this._patternToRegExp(qs);
				q.toString = function() {return qs;};
			}

			// set _lastQuery, *then* start the timeout
			// otherwise, if the user types and the last query returns before the timeout,
			// _lastQuery won't be set and their input gets rewritten
			currentQuery = this._lastQuery = query[this.searchAttr] = q;
			this._queryDeferHandle = this.defer(startQuery, this.searchDelay);

			// CENTRIC - START
			if (bAddClass && this.dropDownCssClass) {
				domClass.add(this.dropDown.domNode, this.dropDownCssClass);
			}
			// CENTRIC - END			
		},

		_startSearchFromInput: function() {
			// summary: search using real _lastInput, rather than with prefix that was auto-completed
			const {dropDown, focusNode, hasPlaceholder, _csiPrefix} = this;
			let s = focusNode.value;

			if (_csiPrefix && (s.indexOf(_csiPrefix) === 0)) {
				s = s.substring(_csiPrefix.length);
			}

			this.item = undefined; // undefined means item needs to be set

			// for CheckedMultiSelectInDropdown and FilteringSelectInDropdown,  in the case of IME(input method editor) mode, only start searching if done with picking word, otherwise, the IME would close and end up with failing to pick word
			if (this.extraTextBox?.textbox?.isComposisiton) {
				if (this.extraTextBox.textbox.isDoneComposition) {
					this._startSearch(s);
				}

			} else {
				this._startSearch(s);
			}

			// open drop down if there is a placeholder
			if (hasPlaceholder) {
				if (this.dropDown) {
					this.openDropDown();
				} else {
					this.loadAndOpenDropDown();
				}
			}
		},

		_preRequest: function(res) {
			// summary: update background color when fetching
			if (this._buttonNode) {
				domStyle.set(this._buttonNode, "background", "var(--DropdownPreRequestBackgroundColor)");
			}
			return res;
		},

		_postRequest: function(res) {
			// summary: reset background color when done fetching
			if (this._buttonNode) {
				domStyle.set(this._buttonNode, "background", "");
			}
			return res;
		},

		// TODO: delaySearch - modify/override functions below as needed to support multi-select

		/**
		 * Return hint to use in combo box placeholder when delaying search
		 * @return {string}
		 */
		getDelaySearchHint() {
			const displayedValue = this.get("displayedValue");
			return searchStringEquals(displayedValue, this.initialDisplayedValue) ? "" : locale.resource("TypeEscapeToRevert");
		},

		/**
		 * @override
		 * Add hint if user starts typing to find a different value than what is already selected
		 */
		_onKey(evt) {
			const displayedValue = this.get("displayedValue");
			const item = this.get("item");
			const {dropDown, hasPlaceholder, initialDisplayedValue, store} = this;
			const {keyCode} = evt;
			const _store = store.sourceStore || store;

			if (hasPlaceholder) {
				// start delayed search once text is entered and user hits ENTER
				// NOTE: there is similar code in SelectController to handle inline editing - this code is not used in that case
				// do not start search if user clicks enter for highlighted menu item
				if (displayedValue && (keyCode === keys.ENTER) && (!item || !_store.getIdentity(item)) && (!this.dropDown.getHighlightedOption())) {
					store.skipDelayOnNextQuery = true;
					this._startSearch(displayedValue);
					evt.preventDefault();
					return;
				}

				// store initial displayed value if not already set
				if (initialDisplayedValue == null) {
					this.initialDisplayedValue = displayedValue;
				}

				// use timeout to allow the key to be fully processed before determining appropriate hint
				setTimeout(() => {
					dropDown?.placeholderNode?.set("hint", this.getDelaySearchHint());
				}, 1);
			}

			return this.inherited(arguments);
		},

		showConfirmOnClearSelectionDialog(oldValue, oldDisplayedValue, onDontShowAgain, processNewValue) {
			const name = "DoNotShow";

			new MessageDialog({
				buttons: [MessageDialog.BUTTON_CONTINUE, MessageDialog.BUTTON_CANCEL],
				materialIcon: MessageDialog.ICON_WARNING,
				message: locale.resource("ClearPreviousValueMessage", [oldDisplayedValue]),
				title: locale.resource("ClearPreviousValueTitle"),

				onMessage: (element) => {
					const div = html.create("div", {className: "csi-message-checkbox"}, element);
					const checkbox = new CheckBox({
						name,
						value: false,
					}).placeAt(div);
					checkbox.startup();
					html.create("span", {innerText: locale.resource("DoNotShowMessageAgain")}, div);
				},

				onResponse: (key, values) => {
					if (onDontShowAgain) {
						const dontShowAgainCheckboxValue = !!values[name]?.[0];
						onDontShowAgain(dontShowAgainCheckboxValue);
					}

					if (key == MessageDialog.BUTTON_CANCEL) {
						processNewValue(oldValue);
					} else {
						processNewValue(csi.EMPTY_URL);
					}
				}
			}).show();
		}
	});
});